import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import SkeletonLoader from '../components/SkeletonLoader';

interface Stats {
  agents: number;
  projects: number;
  documents: number;
}

interface GaugeData {
  label: string;
  value: number;
  max: number;
  color: string;
  colorClass: string;
  trailClass: string;
}

interface Agent {
  name: string;
  status: 'healthy' | 'warning' | 'error';
  heartbeat: string;
  tasks: number;
  latency: string;
}

interface ActivityEvent {
  id: number;
  text: string;
  time: string;
}

const MOCK_AGENTS: Agent[] = [
  { name: 'Research Bot', status: 'healthy', heartbeat: '2s ago', tasks: 23, latency: '1.2s' },
  { name: 'Code Reviewer', status: 'healthy', heartbeat: '5s ago', tasks: 8, latency: '2.1s' },
  { name: 'Data Analyst', status: 'warning', heartbeat: '30s ago', tasks: 15, latency: '4.5s' },
  { name: 'Report Generator', status: 'healthy', heartbeat: '1s ago', tasks: 5, latency: '0.8s' },
  { name: 'Web Scraper', status: 'error', heartbeat: '2m ago', tasks: 0, latency: '—' },
];

const MOCK_ACTIVITY: string[] = [
  'Agent "Research Bot" completed task: Market Analysis',
  'New document uploaded: Q4 Report.pdf',
  'Project "Product Roadmap" updated by Phil',
  'Agent "Code Reviewer" flagged issue in PR #42',
  'Swarm "Customer Support" started processing batch',
  'Knowledge base updated with 3 new documents',
  'Agent "Data Analyst" completed sentiment analysis',
];

const QUICK_ACTIONS = [
  { label: 'Create Swarm', path: '/swarm', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
  { label: 'View Analytics', path: '/analytics', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
  { label: 'New Project', path: '/projects', icon: 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z' },
  { label: 'Manage Agents', path: '/agents', icon: 'M12 4v16m8-8H4' },
  { label: 'Open Knowledge', path: '/knowledge', icon: 'M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12' },
  { label: 'Settings', path: '/settings', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z' },
];

function AnimatedGauge({ gauge, delay }: { gauge: GaugeData; delay: number }) {
  const [mounted, setMounted] = useState(false);
  const percentage = (gauge.value / gauge.max) * 100;
  const circumference = 2 * Math.PI * 42;
  const offset = circumference - (percentage / 100) * circumference;

  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 flex flex-col items-center justify-center relative overflow-hidden group hover:shadow-md transition-shadow duration-300">
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br from-purple-500/5 via-transparent to-blue-500/5 pointer-events-none" />
      <div className="relative w-28 h-28">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
          <circle
            cx="50"
            cy="50"
            r="42"
            fill="none"
            strokeWidth="8"
            className={gauge.trailClass}
            strokeLinecap="round"
          />
          <circle
            cx="50"
            cy="50"
            r="42"
            fill="none"
            strokeWidth="8"
            className={gauge.colorClass}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={mounted ? offset : circumference}
            style={{ transition: 'stroke-dashoffset 1.2s cubic-bezier(0.4, 0, 0.2, 1)' }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-2xl font-bold text-gray-800 dark:text-gray-100">
            {gauge.value}{gauge.max === 100 ? '%' : ''}
          </span>
        </div>
      </div>
      <p className="mt-3 text-sm font-medium text-gray-500 dark:text-gray-400">{gauge.label}</p>
    </div>
  );
}

function ProgressBar({ used, limit, color }: { used: number; limit: number; color: string }) {
  const pct = Math.min((used / limit) * 100, 100);
  return (
    <div className="w-full h-2.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
      <div
        className={`h-full rounded-full ${color} transition-all duration-1000 ease-out`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

const BusinessMetricCard = ({ title, value, icon, color }: { title: string; value: string | number; icon: React.ReactNode; color: string }) => {
  const colorClasses: Record<string, string> = {
    green: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400',
    blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
    purple: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
    orange: 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400',
  };
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          {icon}
        </div>
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400">{title}</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white">{value}</p>
        </div>
      </div>
    </div>
  );
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [, setStats] = useState<Stats>({ agents: 0, projects: 0, documents: 0 });
  const [loading, setLoading] = useState(true);
  const [activity, setActivity] = useState<ActivityEvent[]>([]);
  const feedRef = useRef<HTMLDivElement>(null);
  const activityIndex = useRef(0);
  const [businessMetrics, setBusinessMetrics] = useState({
    tasksCompleted: 0,
    timeSavedHours: 0,
    costSavings: '0.00',
    activeProjects: 0,
  });

  useEffect(() => {
    let cancelled = false;
    const fetchStats = async () => {
      try {
        const response = await api.get('/api/dashboard');
        if (!cancelled) {
          setStats(response.data);
          const tasksCompleted = MOCK_AGENTS.reduce((sum, a) => sum + a.tasks, 0);
          setBusinessMetrics({
            tasksCompleted,
            timeSavedHours: Math.round(tasksCompleted * 0.75),
            costSavings: (tasksCompleted * 0.02).toFixed(2),
            activeProjects: response.data.projects || 0,
          });
        }
      } catch {
        if (!cancelled) {
          setStats({ agents: 12, projects: 8, documents: 156 });
          const tasksCompleted = MOCK_AGENTS.reduce((sum, a) => sum + a.tasks, 0);
          setBusinessMetrics({
            tasksCompleted,
            timeSavedHours: Math.round(tasksCompleted * 0.75),
            costSavings: (tasksCompleted * 0.02).toFixed(2),
            activeProjects: 8,
          });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    fetchStats();
    return () => { cancelled = true; };
  }, []);

  // Populate activity feed with staggered entries
  useEffect(() => {
    const addEvent = () => {
      const text = MOCK_ACTIVITY[activityIndex.current % MOCK_ACTIVITY.length];
      const now = new Date();
      const time = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      setActivity((prev) => {
        const next = [{ id: Date.now(), text, time }, ...prev];
        return next.slice(0, 20);
      });
      activityIndex.current++;
    };

    addEvent();
    const interval = setInterval(addEvent, 4000);
    return () => clearInterval(interval);
  }, []);

  const gauges: GaugeData[] = [
    { label: 'CPU Usage', value: 68, max: 100, color: 'purple', colorClass: 'stroke-purple-500', trailClass: 'stroke-purple-100 dark:stroke-gray-700' },
    { label: 'Memory Usage', value: 54, max: 100, color: 'blue', colorClass: 'stroke-blue-500', trailClass: 'stroke-blue-100 dark:stroke-gray-700' },
    { label: 'Active Tasks', value: 7, max: 10, color: 'green', colorClass: 'stroke-green-500', trailClass: 'stroke-green-100 dark:stroke-gray-700' },
    { label: 'Token Usage', value: 45, max: 100, color: 'orange', colorClass: 'stroke-orange-500', trailClass: 'stroke-orange-100 dark:stroke-gray-700' },
  ];

  const tokenData = { used: 45230, limit: 100000, input: 28100, output: 15630, cached: 1500, resetDate: 'Jul 1, 2026' };

  const statusStyles: Record<string, { dot: string; badge: string }> = {
    healthy: { dot: 'bg-green-500', badge: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
    warning: { dot: 'bg-yellow-500', badge: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' },
    error: { dot: 'bg-red-500', badge: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  };

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Gauge skeletons */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <SkeletonLoader count={4} className="flex justify-center" />
        </div>
        {/* Business metric skeletons */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <SkeletonLoader count={4} />
        </div>
        {/* Content skeletons */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <SkeletonLoader count={6} />
          <SkeletonLoader count={6} />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <SkeletonLoader count={6} />
          <SkeletonLoader count={6} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Live Gauges Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {gauges.map((g, i) => (
          <AnimatedGauge key={g.label} gauge={g} delay={i * 120} />
        ))}
      </div>

      {/* Business Value Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <BusinessMetricCard
          title="Tasks Completed"
          value={businessMetrics.tasksCompleted}
          icon={
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
          color="green"
        />
        <BusinessMetricCard
          title="Time Saved"
          value={`${businessMetrics.timeSavedHours}h`}
          icon={
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
          color="blue"
        />
        <BusinessMetricCard
          title="Cost Savings"
          value={`$${businessMetrics.costSavings}`}
          icon={
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
          color="purple"
        />
        <BusinessMetricCard
          title="Active Projects"
          value={businessMetrics.activeProjects}
          icon={
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
            </svg>
          }
          color="orange"
        />
      </div>

      {/* Token Usage Detail + Agent Health */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Token Usage Detail */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Token Usage</h2>
            <span className="text-xs font-medium text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 px-2.5 py-1 rounded-full">
              Resets {tokenData.resetDate}
            </span>
          </div>

          <div className="mb-4">
            <div className="flex items-baseline justify-between mb-1.5">
              <span className="text-3xl font-bold text-gray-800 dark:text-gray-100">
                {tokenData.used.toLocaleString()}
              </span>
              <span className="text-sm text-gray-400 dark:text-gray-500">
                / {tokenData.limit.toLocaleString()}
              </span>
            </div>
            <ProgressBar used={tokenData.used} limit={tokenData.limit} color="bg-purple-500" />
          </div>

          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'Input', value: tokenData.input, color: 'bg-blue-400' },
              { label: 'Output', value: tokenData.output, color: 'bg-green-400' },
              { label: 'Cached', value: tokenData.cached, color: 'bg-amber-400' },
            ].map((item) => (
              <div key={item.label} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="flex items-center gap-1.5 mb-1">
                  <div className={`w-2 h-2 rounded-full ${item.color}`} />
                  <span className="text-xs text-gray-500 dark:text-gray-400">{item.label}</span>
                </div>
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                  {item.value.toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Agent Health Cards */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">Agent Health</h2>
          <div className="space-y-3">
            {MOCK_AGENTS.map((agent) => {
              const style = statusStyles[agent.status];
              return (
                <div
                  key={agent.name}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-2.5 h-2.5 rounded-full ${style.dot} ${agent.status === 'healthy' ? 'animate-pulse' : ''}`} />
                    <div>
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-100">{agent.name}</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500">Heartbeat {agent.heartbeat}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-xs text-gray-400 dark:text-gray-500">Tasks</p>
                      <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">{agent.tasks}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-400 dark:text-gray-500">Latency</p>
                      <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">{agent.latency}</p>
                    </div>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${style.badge}`}>
                      {agent.status}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Activity Feed + Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Real-Time Activity Feed */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 flex flex-col" style={{ maxHeight: 380 }}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Activity Feed</h2>
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
            </span>
          </div>
          <div ref={feedRef} className="flex-1 overflow-y-auto space-y-2 pr-1 scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-gray-700">
            {activity.map((event, i) => (
              <div
                key={event.id}
                className="flex items-start gap-3 p-2.5 rounded-lg bg-gray-50 dark:bg-gray-700/40 transition-all duration-500"
                style={{ animation: i === 0 ? 'fadeSlideIn 0.5s ease-out' : undefined }}
              >
                <div className="w-2 h-2 mt-1.5 rounded-full bg-purple-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{event.text}</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{event.time}</p>
                </div>
              </div>
            ))}
          </div>
          <style>{`
            @keyframes fadeSlideIn {
              from { opacity: 0; transform: translateY(-8px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}</style>
        </div>

        {/* Quick Actions */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 gap-3">
            {QUICK_ACTIONS.map((action) => (
              <button
                key={action.label}
                onClick={() => navigate(action.path)}
                className="flex items-center gap-2.5 p-3.5 bg-gray-50 dark:bg-gray-700 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-purple-50 hover:text-purple-600 dark:hover:bg-purple-900/20 dark:hover:text-purple-400 transition-all duration-200 group cursor-pointer text-left"
              >
                <svg
                  className="w-4 h-4 text-gray-400 group-hover:text-purple-500 transition-colors duration-200"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d={action.icon} />
                </svg>
                {action.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
